<template>
  <div id="room-info">
    <div id="tab">
      <div
        class="tab"
        :class="{ active: listType === 'about' }"
        @click="changeListType('about')"
      >
        About
      </div>
      <div
        class="tab"
        :class="{ active: listType === 'members' }"
        @click="changeListType('members')"
      >
        Members
      </div>
      <div
        v-if="isAdmin"
        class="tab"
        :class="{ active: listType === 'sent' }"
        @click="changeListType('sent')"
      >
        Invited
      </div>
    </div>

    <transition name="fade">
      <comp-message
        v-if="showMessage"
        :msgText="messageText"
        :msgSuccess="!messageIsErr"
        :msgError="messageIsErr"
        @closeMsg="showMessage = false"
      />
    </transition>

    <!-- About tab -->
    <div class="contents" v-if="listType === 'about'">
      <div class="column">
        <div id="radar">
          <div class="radar-wrapper">
            <div class="radar-container">
              <div
                class="section"
                v-for="section in sectionPositionCount"
                :key="section"
                :class="`pos${section}`"
              >
                <div class="box-container">
                  <div v-for="level in zoneLevelDepth" :key="level">
                    <div class="box" :class="`c${level}`">
                      <div class="field">
                        <div v-for="(member, idx) in memberList" :key="idx">
                          <div
                            class="user"
                            v-if="
                              chkMemberExistZoneInRadar(member, section, level)
                            "
                            v-once
                            :style="{
                              backgroundImage: `url(data:image/png;base64,${member.partnerImage})`,
                              top: `${Math.random() * 10}px`,
                              left: `${Math.random() * (15 * (level + 1))}px`,
                              transform: `rotate(${340 - section * 40}deg)`
                            }"
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="button-group">
          <div class="button" @click="onRoomDetailClick">
            Your Team Analysis Result
          </div>
        </div>

        <!-- Zone blocks -->
        <div class="row">
          <div
            class="block"
            v-for="(zoneBlock, idx) in getZoneBlockList"
            :key="idx"
            :class="{ active: selectedZone == zoneBlock[0].zone && !isMobile }"
            @click="onZoneClick(zoneBlock)"
          >
            <div class="title">{{ zoneBlock[0].zone }}</div>
            <div class="sub">{{ `${zoneBlock.length} People` }}</div>
            <div class="members">
              <div
                class="profile"
                v-for="(member, memIdx) in zoneBlock"
                :key="memIdx"
                :class="{ me: userSeqId == member.partnerSeqId }"
                :style="{
                  backgroundImage:
                    userSeqId != member.partnerSeqId &&
                    `url(data:image/png;base64,${member.partnerImage})`
                }"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Zone description (Desktop) -->
      <div class="column zone-container" v-if="!isMobile">
        <h1 class="zone">{{ selectedZone }}</h1>

        <div class="zone">
          <h4>Zone Description</h4>
          {{ zoneDescription }}
        </div>

        <comp-profile
          v-for="(member, idx) in getSelectedZoneMemberList"
          :key="idx"
          :itsMe="member.partnerSeqId == userSeqId"
          :photoImage="member.partnerImage"
          :profileName="member.partnerName"
          :profileSub="member.partnerType || 'Unknown'"
          :hasButton="true"
          :btnRoomSpecific="member.partnerSeqId != userSeqId"
          :invitePending="getInvitePendingStatus(member)"
          :isAdmin="isAdmin"
          @click.native.stop="onMemberClick(member)"
          @delete-click="performKickMember(member.memberSeqId)"
          @invite-click="performConnectionInvite(member.partnerUserId)"
        />
      </div>
    </div>

    <!-- Members tab -->
    <div v-else-if="listType === 'members'">
      <comp-profile
        v-for="member in getMemberList"
        :key="member.memberSeqId"
        :itsMe="member.partnerSeqId == userSeqId"
        :photoImage="member.partnerImage"
        :profileName="member.partnerName"
        :profileSub="member.partnerType || 'Unknown'"
        :hasButton="true"
        :btnRoomSpecific="member.partnerSeqId != userSeqId"
        :invitePending="getInvitePendingStatus(member)"
        :isAdmin="isAdmin"
        :masterGrantMode="masterGrantMode"
        :isMaster="roomMasterPartnerSeqId === member.partnerSeqId"
        :isMasterCandidate="candidateSeqId === member.memberSeqId"
        @click.native.stop="onMemberClick(member)"
        @delete-click="performKickMember(member.memberSeqId)"
        @invite-click="performConnectionInvite(member.partnerUserId)"
        @mgm-on="switchMasterGrantMode"
        @mgm-off="switchMasterGrantMode"
        @mgm-modal="switchModalMasterGrantMode"
      />
    </div>

    <!-- Sent tab -->
    <div v-else-if="listType === 'sent'">
      <comp-profile
        v-for="member in sentList"
        :key="member.memberSeqId"
        :photoImage="member.partnerImage"
        :profileName="member.partnerName"
        :profileSub="member.partnerUserId"
        :hasButton="true"
        :btnInviteSent="true"
        @resend="performResendEmailInvite(member.partnerUserId)"
        @dismiss="performCancelInvite(member)"
      />
    </div>

    <!-- Invite modal -->
    <div class="modal" id="invite" v-if="modalInvite">
      <div class="modal-contents">
        <div class="modal-header">Send Invitation</div>
        <div class="modal-body">
          <div class="form-container">
            <label class="label" for="inviteemail">Invite via Email</label>
            <input
              type="text"
              name=""
              id="inviteemail"
              v-model.trim="inviteUserEmail"
            />
          </div>
        </div>
        <div class="modal-footer">
          <div class="button-group">
            <div class="button-container">
              <div class="button" @click="modalInvite = false">Cancel</div>
            </div>
            <div class="button-container">
              <div class="button" @click="performSendEmailInvite">OK</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Change master modal -->
    <div class="modal" id="change-master" v-if="modalMasterGrantMode">
      <div class="modal-contents">
        <div class="modal-header">Change master</div>
        <div class="modal-body">Are you sure?</div>
        <div class="modal-footer">
          <div class="button-group">
            <div class="button-container">
              <div class="button" @click="modalMasterGrantMode = false">
                Cancel
              </div>
            </div>
            <div class="button-container">
              <div class="button" @click="grantRoomMaster">OK</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit room modal -->
    <div class="modal full-on-mobile" id="edit-room" v-if="modalEditRoom">
      <div class="modal-contents">
        <div class="modal-header">Edit Room</div>
        <div class="modal-body">
          <div class="form-group">
            <div class="profile-photo">
              <div class="label">Room Photo</div>
              <div class="photo-wrapper">
                <div class="photo-container" :style="updatePhotoView()"></div>
                <div class="button-container">
                  <!-- <div class="btn-upload">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path
                        d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"
                      ></path>
                      <circle cx="12" cy="13" r="4"></circle>
                    </svg>
                  </div> -->
                  <input
                    type="file"
                    class="btn-upload"
                    @change="onFileSelected($event.target.files)"
                    @click="onFileUploadClick"
                    ref="docFile"
                    accept=".jpg, .jpeg, .png"
                    id="fileEvent"
                    hidden
                  />
                  <div class="btn-upload" @click="onFileUploadClickTrigger">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <rect
                        x="3"
                        y="3"
                        width="18"
                        height="18"
                        rx="2"
                        ry="2"
                      ></rect>
                      <circle cx="8.5" cy="8.5" r="1.5"></circle>
                      <polyline points="21 15 16 10 5 21"></polyline>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            <div class="form-container">
              <label class="label" for="roomname">Room Name</label>
              <input
                type="text"
                name=""
                id="roomname"
                v-model.trim="roomName"
              />
            </div>
            <div class="form-container">
              <label class="label" for="companyname">Company Name</label>
              <input
                type="text"
                name=""
                id="companyname"
                v-model.trim="roomCompanyName"
              />
            </div>
            <div class="form-container">
              <label class="label" for="roomname">Description</label>
              <textarea
                name=""
                id=""
                cols="30"
                rows="5"
                v-model.trim="roomDesc"
              ></textarea>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <div class="button-group">
            <div class="button-container">
              <div class="button" @click="modalEditRoom = false">Cancel</div>
            </div>
            <div class="button-container">
              <div class="button" @click="performEditRoom">
                <comp-loading v-if="isLoading"></comp-loading>
                <div v-else>OK</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="btn-add" v-if="addBtnConstraints()" @click="modalInvite = true">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <line x1="5" y1="12" x2="19" y2="12"></line>
      </svg>
    </div>

    <comp-loading v-if="isLoading" :fullscreen="true"></comp-loading>
  </div>
</template>

<script>
import common from '@/utils/common'
import RoomService from '@/services/room-service'
import CompMessage from '@/components/CompMessage'
import CompProfile from '@/components/CompProfile'
import CompLoading from '@/components/CompLoading'

export default {
  name: 'RoomInfo',
  mixins: [common],
  components: {
    CompMessage,
    CompProfile,
    CompLoading
  },
  created() {
    this.$parent.setNav(true)
    this.$parent.setBottomTab(false)

    this.$EventBus.$on('open-local-menu', () => {
      this.modalEditRoom = true
    })

    this.$EventBus.$on('addSomething', () => {
      this.modalInvite = true
    })

    this.performGetRoomInfo()
    this.changeListType(this.listType)
  },
  data() {
    return {
      sectionPositionCount: [0, 1, 2, 3, 4, 5, 6, 7, 8],
      zoneTypeClockwise: ['W3', 'B2', 'W2', 'W1', 'B1', 'N1', 'N2', 'N3', 'B3'],
      zoneLevelDepth: [0, 1, 2],

      roomSeqId: this.$route.params.roomSeqId,
      userSeqId: this.$store.state.auth.user.userSeqId,

      showMessage: false,
      messageText: '',
      messageIsErr: false,

      userName: 'User Name',
      userType: 'User Type',
      defineRelationship: false,
      modalEditRoom: false,
      modalInvite: false,
      modalMasterGrantMode: false,
      relDefined: true,

      listType: 'about',
      zoneDescription: '',
      selectedZone: '',

      isAdmin: false,
      memberList: [],
      nZoneList: [[]],
      bZoneList: [[]],
      wZoneList: [[]],
      sentList: [],
      inviteUserEmail: '',

      roomMasterPartnerSeqId: null,
      masterGrantMode: false,
      candidateSeqId: null,

      roomPhoto: null,
      roomName: '',
      roomCompanyName: '',
      roomDesc: '',

      isLoading: false
    }
  },
  computed: {
    isMobile() {
      return this.$store.state.isMobile
    },
    getZoneBlockList() {
      const nZoneList = this.nZoneList.filter(zone => zone.length > 0)
      const bZoneList = this.bZoneList.filter(zone => zone.length > 0)
      const wZoneList = this.wZoneList.filter(zone => zone.length > 0)

      var zoneBlockList = []
      if (nZoneList.length > 0) {
        zoneBlockList = zoneBlockList.concat(nZoneList)
      }
      if (bZoneList.length > 0) {
        zoneBlockList = zoneBlockList.concat(bZoneList)
      }
      if (wZoneList.length > 0) {
        zoneBlockList = zoneBlockList.concat(wZoneList)
      }
      return zoneBlockList
    },
    getSelectedZoneMemberList() {
      const memberList = []
      this.getZoneBlockList.forEach(zoneBlock => {
        if (zoneBlock[0].zone === this.selectedZone) {
          zoneBlock.forEach(member => {
            memberList.push(member)
          })
        }
      })
      return memberList
    },
    getMemberList() {
      if (this.masterGrantMode) {
        return this.memberList.filter(member => {
          return (
            this.userSeqId === member.partnerSeqId ||
            member.connectionSeqId !== 0
          )
        })
      }
      return this.memberList
    }
  },
  methods: {
    performGetRoomInfo() {
      this.isLoading = true

      if (this.roomSeqId) {
        RoomService.getRoomInfo(this.roomSeqId).then(
          ({ resultCode, resultData, resultMsg }) => {
            this.isLoading = false

            if (resultCode === 0) {
              if (!this.isEmpty(resultData)) {
                this.isAdmin =
                  resultData.roomMasterPartnerSeqId === this.userSeqId
                this.roomMasterPartnerSeqId = resultData.roomMasterPartnerSeqId
                this.roomPhoto = resultData.image
                this.roomName = resultData.name
                this.roomCompanyName = resultData.company
                this.roomDesc = resultData.description

                this.memberList = resultData.memberList
                this.sentList = resultData.sentList

                this.$parent.setHeader(this.roomName)
                this.$parent.headerBack = true
                this.$parent.headerEdit = this.isAdmin
                this.updateMemberRadar()
              }
            } else {
              this.showInfoMessage(resultMsg, true)
            }
          }
        )
      }
    },
    performConnectionInvite(partnerEmail) {
      this.isLoading = true

      const params = {
        userIdList: [partnerEmail]
      }
      RoomService.sendEmailInvite(params).then(({ resultCode, resultMsg }) => {
        this.showInfoMessage(resultMsg, resultCode !== 0)

        if (resultCode === 0) {
          this.performGetRoomInfo()
        }
      })
    },
    performSendEmailInvite() {
      if (!this.isEmpty(this.inviteUserEmail)) {
        this.isLoading = true

        const params = {
          roomSeqId: this.roomSeqId,
          userIdList: [this.inviteUserEmail]
        }
        RoomService.sendEmailInvite(params).then(
          ({ resultCode, resultMsg }) => {
            this.showInfoMessage(resultMsg, resultCode !== 0)

            if (resultCode === 0) {
              this.inviteUserEmail = ''
              this.modalInvite = false
            }
          }
        )
      }
    },
    performKickMember(memberSeqId) {
      this.isLoading = true

      RoomService.kickUserFromRoom(this.roomSeqId, memberSeqId).then(
        ({ resultCode, resultMsg }) => {
          this.showInfoMessage(resultMsg, resultCode !== 0)

          if (resultCode === 0) {
            this.performGetRoomInfo()
          }
        }
      )
    },
    performResendEmailInvite(inviteUserEmail) {
      if (!this.isEmpty(inviteUserEmail)) {
        this.isLoading = true

        const params = {
          roomSeqId: this.roomSeqId,
          userIdList: [inviteUserEmail]
        }
        RoomService.sendEmailInvite(params).then(
          ({ resultCode, resultMsg }) => {
            this.showInfoMessage(resultMsg, resultCode !== 0)

            if (resultCode === 0) {
              this.inviteUserEmail = ''
              this.performGetRoomInfo()
            }
          }
        )
      }
    },
    performCancelInvite(member) {
      this.isLoading = true

      const memberSeqId = member.memberSeqId
      const partnerSeqId = member.partnerSeqId

      RoomService.cancelEmailInvite(memberSeqId, partnerSeqId).then(
        ({ resultCode, resultMsg }) => {
          this.showInfoMessage(resultMsg, resultCode !== 0)

          if (resultCode === 0) {
            this.performGetRoomInfo()
          }
        }
      )
    },
    async performEditRoom() {
      const roomName = this.roomName
      const companyName = this.roomCompanyName
      const desc = this.roomDesc

      if (
        !this.isEmpty(roomName) &&
        !this.isEmpty(companyName) &&
        !this.isEmpty(desc)
      ) {
        this.isLoading = true

        const roomData = {
          roomSeqId: this.roomSeqId,
          roomName,
          companyName,
          desc,
          roomPhoto: this.roomPhoto
        }
        await RoomService.editRoom(roomData).then(
          ({ resultCode, resultMsg }) => {
            if (resultCode === 0) {
              this.showInfoMessage(resultMsg, resultCode !== 0)

              this.modalEditRoom = false

              this.roomName = ''
              this.roomCompanyName = ''
              this.roomDesc = ''
              this.roomPhoto = null

              this.performGetRoomInfo()
            }
          }
        )
      }
    },
    performGetZoneDescription(zone) {
      this.isLoading = true

      this.selectedZone = zone

      RoomService.getZoneDescription(zone).then(
        ({ resultCode, resultData }) => {
          this.isLoading = false

          if (resultCode === 0 && !this.isEmpty(resultData)) {
            this.zoneDescription = resultData
          }
        }
      )
    },
    showInfoMessage(message, isError) {
      this.isLoading = false

      this.showMessage = true
      this.messageText = message
      this.messageIsErr = isError || false

      setTimeout(() => {
        this.showMessage = false
        this.messageText = ''
        this.messageIsErr = false
      }, 2000)
    },
    updateMemberRadar() {
      const n1List = []
      const n2List = []
      const n3List = []

      const b1List = []
      const b2List = []
      const b3List = []

      const w1List = []
      const w2List = []
      const w3List = []

      this.memberList.forEach(member => {
        if (member.partnerSeqId === this.userSeqId) {
          if (this.selectedZone !== member.zone) {
            this.selectedZone = member.zone
            if (!this.isMobile) this.performGetZoneDescription(member.zone)
          }
        }
        switch (member.zone) {
          case 'N1':
            n1List.push(member)
            break
          case 'N2':
            n2List.push(member)
            break
          case 'N3':
            n3List.push(member)
            break
          case 'B1':
            b1List.push(member)
            break
          case 'B2':
            b2List.push(member)
            break
          case 'B3':
            b3List.push(member)
            break
          case 'W1':
            w1List.push(member)
            break
          case 'W2':
            w2List.push(member)
            break
          case 'W3':
            w3List.push(member)
            break
        }
      })
      this.nZoneList = [n1List, n2List, n3List]
      this.bZoneList = [b1List, b2List, b3List]
      this.wZoneList = [w1List, w2List, w3List]

      this.isLoading = false
    },
    chkMemberExistZoneInRadar(member, section, level) {
      if (this.getZonePosInClockwise(member) === section) {
        return this.getZoneLevelPosFromLevel(member) === level
      }
    },
    getZonePosInClockwise(member) {
      return this.zoneTypeClockwise.indexOf(member.zone)
    },
    getZoneLevelPosFromLevel(member) {
      const zoneLevelPos = parseInt(
        (member.zoneLevel - 1) / this.zoneLevelDepth.length
      )
      if (zoneLevelPos < 0) return 0
      if (zoneLevelPos > this.zoneLevelDepth[this.zoneLevelDepth.length - 1]) {
        return this.zoneLevelDepth[this.zoneLevelDepth.length - 1]
      }
      return zoneLevelPos
    },
    setPositioningMembersInRadar() {
      this.memberList.forEach(member => {
        let user = document.getElementById(`user${member.memberSeqId}`)
        let position = user.getBoundingClientRect()

        const rnd = this.getRandom(member)
        let x = position.left + rnd[0]
        let y = position.top + rnd[1]

        document.getElementById(`user-${member.memberSeqId}`).style.top =
          y + 'px'
        document.getElementById(`user-${member.memberSeqId}`).style.left =
          x + 'px'
      })
    },
    getRandom(member) {
      const section = this.getZonePosInClockwise(member)
      const level = this.getZoneLevelPosFromLevel(member) + 1
      switch (section) {
        case 0:
          return [
            Math.floor(Math.random() * (15 * level)),
            Math.floor(Math.random() * (5 * level))
          ]
        case 1:
          return [
            Math.floor(Math.random() * (3 * level)) + level,
            Math.floor(Math.random() * (10 * level))
          ]
        case 2:
          return [
            Math.floor(Math.random() * (2 * level)),
            Math.floor(Math.random() * (15 * level))
          ]
        case 3:
          return [
            -Math.floor(Math.random() * (5 * level)) - level * 4,
            Math.floor(Math.random() * (12 * level))
          ]
        case 4:
          return [
            Math.floor(Math.random() * (15 * level)),
            Math.floor(Math.random() * (5 * level))
          ]
        case 5:
          return [
            -Math.floor(Math.random() * (12 * level)),
            -Math.floor(Math.random() * (5 * level)) - level * 4
          ]
        case 6:
          return [
            Math.floor(Math.random() * (2 * level)),
            -Math.floor(Math.random() * (15 * level))
          ]
        case 7:
          return [
            Math.floor(Math.random() * (3 * level)) + level,
            -Math.floor(Math.random() * (10 * level))
          ]
        case 8:
          return [
            Math.floor(Math.random() * (15 * level)),
            Math.floor(Math.random() * (5 * level))
          ]
      }
      return [0, 0]
    },
    updatePhotoView() {
      if (this.roomPhoto) {
        return {
          backgroundImage: `url(data:image/png;base64,${this.roomPhoto})`
        }
      }
    },
    changeListType(listType) {
      this.listType = listType
      if (listType === 'members' && this.isAdmin) {
        this.$parent.addSomething = 'Add Member'
      } else {
        this.$parent.addSomething = ''
      }
    },
    getInvitePendingStatus(member) {
      const connectionStatus = member.connectionStatus
      if (
        this.isEmpty(connectionStatus) ||
        connectionStatus === 'disconnected'
      ) {
        return 'none'
      } else if (connectionStatus === 'connecting') {
        return 'pending'
      }
      return 'connected'
    },
    addBtnConstraints() {
      if (this.listType === 'members' && this.isAdmin) {
        return this.isMobile && !this.modalInvite
      }
      return false
    },
    onRoomDetailClick() {
      this.goPageWithQuery(`/roomDetails/${this.roomSeqId}`, {
        roomName: this.roomName
      })
    },
    onZoneClick(zoneBlock) {
      const zone = zoneBlock[0].zone
      if (this.isMobile) {
        this.goPageWithQuery('/roomZoneInfo', {
          isAdmin: this.isAdmin,
          roomSeqId: this.roomSeqId,
          zone,
          members: zoneBlock
        })
      } else if (this.selectedZone !== zone) {
        this.selectedZone = zone
        this.performGetZoneDescription(zone)
      }
    },
    onMemberClick(member) {
      if (member.partnerSeqId === this.userSeqId) {
        return
      }
      if (this.masterGrantMode) {
        if (member.connectionSeqId === 0) {
          this.showInfoMessage('Invalid connection sequence ID.', true)
          return
        }
        this.candidateSeqId = member.memberSeqId
      } else {
        if (member.connectionSeqId !== 0) {
          this.goPage(`connectionbrief/${member.connectionSeqId}`)
        } else {
          this.showInfoMessage('Not a Connection member.', true)
        }
      }
    },
    onFileUploadClickTrigger() {
      var element = document.getElementById('fileEvent')
      element.click()
    },
    onFileUploadClick() {
      this.$refs.docFile.click()
    },
    onFileSelected(files) {
      if (files[0]) {
        this.resizeImage({
          file: files[0],
          maxSize: 500
        }).then(resizedImage => {
          const imgSize = resizedImage.size
          const maxSize = 5 * 1024 * 1024

          if (imgSize > maxSize) {
            this.$parent.showDialog(
              'Error',
              'Photos larger than 5MB cannot be uploaded.',
              () => {}
            )
          } else {
            const photoView = this.$el.querySelector('.photo-container')
            const reader = new FileReader()

            reader.onloadend = () => {
              photoView.style.backgroundImage = `url(${reader.result})`
            }
            reader.readAsDataURL(resizedImage)
            this.roomPhoto = this.convertBlobToFile(resizedImage, 'room.jpg')
          }
        })
      }
    },
    switchMasterGrantMode() {
      if (this.masterGrantMode) {
        this.candidateSeqId = null
        this.masterGrantMode = false
      } else {
        if (this.memberList.length > 1) {
          this.masterGrantMode = true
        } else {
          this.showInfoMessage(
            'There is no member to delegate the master.',
            true
          )
        }
      }
    },
    switchModalMasterGrantMode() {
      this.modalMasterGrantMode = !this.modalMasterGrantMode
    },
    grantRoomMaster() {
      this.isLoading = true
      RoomService.grantRoomMaster(this.roomSeqId, this.candidateSeqId).then(
        ({ resultCode, resultMsg }) => {
          this.showInfoMessage(resultMsg, resultCode !== 0)

          if (resultCode === 0) {
            this.switchModalMasterGrantMode()
            this.switchMasterGrantMode()
            this.performGetRoomInfo()
            this.changeListType(this.listType)
          }
        }
      )
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" src="@/scss/rooms.scss" scoped></style>
